import type * as MediapipeNs from '@mediapipe/tasks-vision';
import type { BBox } from './pii/types';

export type FaceDetection = {
  bbox: BBox;
  confidence: number;
  /** Optional thumbnail data URL for the review UI. */
  thumbnail?: string;
};

type ImageSource = HTMLImageElement | ImageBitmap | HTMLCanvasElement;

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';

type FaceDetectorInstance = Awaited<ReturnType<typeof MediapipeNs.FaceDetector.createFromOptions>>;

/** BlazeFace score threshold. Lower = more recall on profile/occluded
 *  faces at the cost of more false positives on face-like textures.
 *  Tunable here so we don't have to hunt through the file. */
export const FACE_DETECTION_CONFIDENCE = 0.5;

let detectorPromise: Promise<FaceDetectorInstance> | null = null;

const loadDetector = async (): Promise<FaceDetectorInstance> => {
  if (detectorPromise) return detectorPromise;
  detectorPromise = (async (): Promise<FaceDetectorInstance> => {
    const mp: typeof MediapipeNs = await import('@mediapipe/tasks-vision');
    const fileset = await mp.FilesetResolver.forVisionTasks(WASM_BASE);
    return mp.FaceDetector.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: 'CPU' },
      runningMode: 'IMAGE',
      minDetectionConfidence: FACE_DETECTION_CONFIDENCE,
    });
  })();
  return detectorPromise;
};

const dimensionsOf = (source: ImageSource): { width: number; height: number } => {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
};

const cropThumbnail = (source: ImageSource, bbox: BBox, size = 64): string | undefined => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.drawImage(source, bbox.x, bbox.y, bbox.w, bbox.h, 0, 0, size, size);
    return canvas.toDataURL('image/png');
  } catch {
    return undefined;
  }
};

/**
 * Detect faces in an image source. Lazy-loads MediaPipe + the BlazeFace model
 * the first time it's called; returns [] if loading or inference fails.
 */
export const detectFaces = async (source: ImageSource): Promise<FaceDetection[]> => {
  const dims = dimensionsOf(source);
  if (dims.width === 0 || dims.height === 0) return [];

  let detector: FaceDetectorInstance;
  try {
    detector = await loadDetector();
  } catch (cause) {
    console.warn('[anomia] BlazeFace failed to load — face redaction disabled', cause);
    return [];
  }

  let raw;
  try {
    raw = detector.detect(source);
  } catch (cause) {
    console.warn('[anomia] BlazeFace detect() threw', cause);
    return [];
  }

  const out: FaceDetection[] = [];
  for (const det of raw.detections) {
    const box = det.boundingBox;
    if (!box) continue;
    const bbox: BBox = {
      x: Math.max(0, box.originX),
      y: Math.max(0, box.originY),
      w: Math.min(box.width, dims.width - box.originX),
      h: Math.min(box.height, dims.height - box.originY),
    };
    if (bbox.w <= 0 || bbox.h <= 0) continue;
    const score = det.categories[0]?.score ?? 0.5;
    const thumb = cropThumbnail(source, bbox);
    const face: FaceDetection = { bbox, confidence: score };
    if (thumb !== undefined) face.thumbnail = thumb;
    out.push(face);
  }
  return out;
};
